/**
 * `useState` drop-in that persists to `localStorage` under a stable key.
 *
 * Reads the stored value synchronously on mount (never flashes the default)
 * and writes on every update. Safe against QuotaExceeded / malformed-JSON
 * by falling back to the initial value — persistence is UX polish, not a
 * source of truth.
 *
 * The `key` is the storage key; scope it per-feature (e.g.
 * `agentsLab.matrixModels:${agentSpec}`) so state doesn't leak between
 * unrelated views of the same component.
 */
import {useCallback, useEffect, useState} from 'react';

const PREFIX = 'goodshowhq.ui.v1:';

export function usePersistedState<T>(
  key: string,
  initial: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const storageKey = PREFIX + key;

  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw == null) return initial;
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
  });

  // Re-read from storage when the key changes (e.g. switching the focused
  // agent → keyed per-agent state swaps in place).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw == null) {
        setValue(initial);
      } else {
        setValue(JSON.parse(raw) as T);
      }
    } catch {
      setValue(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue(prev => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(resolved));
        } catch {
          // Quota exceeded or storage disabled — ignore. In-memory state still updates.
        }
        return resolved;
      });
    },
    [storageKey],
  );

  return [value, update];
}
